import type { BehaviorDefinition, BehaviorSetting, BehaviorSettings } from '../index.js'
import { Switch } from '../../core/index.js'
import { NumberField } from '../../core/index.js'
import { anchorsAreRates, anchorsDisagree, enabledWeightTotal, sameSetting, hasMinimumOnly, minimumIsInvalid} from './behaviorDrafts.js'

const FULL_WEIGHT = 100
const MAX_COUNT_ANCHOR = 1000

function Badge({ children, tone }: { children: string; tone: 'neutral' | 'custom' | 'against' }) {
  const className = { neutral: 'bg-canvas text-ink-mid border-marine/15', custom: 'bg-marine-lt text-marine-dk border-marine/30', against: 'bg-coral-lt text-coral border-coral/40' }[tone]
  return <span className={`inline-block px-1.5 py-0.5 text-[10px] font-bold border rounded ${className}`}>{children}</span>
}

/** An anchor as the person sees it: a rate as a whole percentage, a count as itself. */
function shownAnchor(value: number, isRate: boolean): number {
  return isRate ? Math.round(value * 100) : value
}

/** The reverse: what the person typed, as the API stores it. */
function storedAnchor(shown: number, isRate: boolean): number {
  const clamped = Math.max(0, Math.min(isRate ? 100 : MAX_COUNT_ANCHOR, shown))
  return isRate ? Math.round(clamped) / 100 : clamped
}

function describeSetting(setting: BehaviorSetting, isRate: boolean, minimumOnly: boolean): string {
  if (!setting.enabled) {
    return 'Off'
  }
  if (minimumOnly) {
    return `On · ${setting.weight}% · 100 from ${setting.best} per period`
  }
  const unit = isRate ? '%' : ''
  return `On · ${setting.weight}% · 100 at ${shownAnchor(setting.best, isRate)}${unit}, 0 at ${shownAnchor(setting.worst, isRate)}${unit}`
}

interface AnchorFieldProps {
  label: string
  behaviorLabel: string
  value: number
  isRate: boolean
  disabled: boolean
  onChange: (value: number) => void
}

function AnchorField({ label, behaviorLabel, value, isRate, disabled, onChange }: AnchorFieldProps) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-ink-mid whitespace-nowrap">{label}</span>
      <NumberField
        value={shownAnchor(value, isRate)}
        label={`${behaviorLabel}: ${label}`}
        suffix={isRate ? '%' : undefined}
        min={0}
        max={isRate ? 100 : MAX_COUNT_ANCHOR}
        disabled={disabled}
        onChange={(shown) => onChange(storedAnchor(shown, isRate))}
      />
    </div>
  )
}

interface BehaviorSettingsTableProps {
  behaviors: readonly BehaviorDefinition[]
  /** The settings being edited, one per behavior. */
  settings: BehaviorSettings
  onChange: (key: string, setting: BehaviorSetting) => void
  /**
   * Given when editing one facility: the master it is measured against. A
   * row that differs is an override and can be reset to it. Omitted when
   * editing the master itself.
   */
  master?: BehaviorSettings
  /** When editing the master: how many of the visible facilities override each behavior. */
  overrideCounts?: Readonly<Record<string, number>>
  disabled?: boolean
}

/**
 * One row per behavior: whether it is measured, how much it counts, the
 * levels that earn 100 and 0 when it is scored, and - for a facility -
 * whether that is the master's setting or the facility's own.
 */
export function BehaviorSettingsTable({ behaviors, settings, onChange, master, overrideCounts, disabled = false }: BehaviorSettingsTableProps) {
  const total = enabledWeightTotal(settings, behaviors)

  return (
    <div>
      <ul className="divide-y divide-marine/10 border border-marine/15 rounded bg-white">
        {behaviors.map((behavior) => {
          const setting = settings[behavior.key] ?? { enabled: false, weight: 0, best: 1, worst: 0 }
          const masterSetting = master?.[behavior.key]
          const isOverride = masterSetting !== undefined && !sameSetting(setting, masterSetting)
          const overriddenAt = overrideCounts?.[behavior.key] ?? 0
          const isRate = anchorsAreRates(behavior)
          const minimumOnly = hasMinimumOnly(behavior)
          const clash = minimumOnly ? minimumIsInvalid(setting) : anchorsDisagree(behavior, setting)
          const update = (changes: Partial<BehaviorSetting>): void => onChange(behavior.key, { ...setting, ...changes })

          return (
            <li key={behavior.key} className={`flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 ${setting.enabled ? '' : 'bg-canvas'}`}>
              <Switch checked={setting.enabled} disabled={disabled} label={`Measure ${behavior.label}`} onChange={(enabled) => update({ enabled })} />

              <div className="min-w-0 flex-1 basis-56">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`text-sm font-bold ${setting.enabled ? 'text-ink' : 'text-ink-mid'}`}>{behavior.label}</span>
                  {behavior.kind === 'custom' && <Badge tone="custom">Custom</Badge>}
                  {behavior.basis === 'per_period' && <Badge tone="neutral">Count per period</Badge>}
                  {behavior.basis === 'per_shift' && <Badge tone="neutral">Count per shift</Badge>}
                  {behavior.basis === 'per_period_minimum' && <Badge tone="neutral">Minimum per period</Badge>}
                  {behavior.direction === 'negative' && <Badge tone="against">Counts against</Badge>}
                  {behavior.eventPath === null && <Badge tone="neutral">No event yet</Badge>}
                </div>
                {behavior.description && <div className="text-[11px] text-ink-mid leading-snug mt-0.5">{behavior.description}</div>}
                {clash && (
                  <div role="alert" className="text-[11px] font-bold text-coral mt-0.5">
                    {minimumOnly
                      ? 'The minimum must be a whole number of 1 or more.'
                      : behavior.direction === 'positive'
                        ? 'Counts for the worker, so the level that earns 100 must be above the level that earns 0.'
                        : 'Counts against the worker, so the level that earns 100 must be below the level that earns 0.'}
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-ink-mid">Weight</span>
                  <NumberField
                    value={setting.weight}
                    label={`Weight of ${behavior.label}`}
                    suffix="%"
                    min={0}
                    max={FULL_WEIGHT}
                    disabled={disabled || !setting.enabled}
                    onChange={(weight) => update({ weight: Math.min(FULL_WEIGHT, Math.max(0, weight)) })}
                  />
                </div>
                {minimumOnly ? (
                  <AnchorField label="100 from" behaviorLabel={behavior.label} value={setting.best} isRate={false} disabled={disabled || !setting.enabled} onChange={(best) => update({ best })} />
                ) : (
                  <>
                    <AnchorField label="100 at" behaviorLabel={behavior.label} value={setting.best} isRate={isRate} disabled={disabled || !setting.enabled} onChange={(best) => update({ best })} />
                    <AnchorField label="0 at" behaviorLabel={behavior.label} value={setting.worst} isRate={isRate} disabled={disabled || !setting.enabled} onChange={(worst) => update({ worst })} />
                  </>
                )}
              </div>

              {masterSetting !== undefined && (
                <div className="w-full sm:w-44 text-[11px]">
                  {isOverride ? (
                    <div className="flex items-center gap-2">
                      <span className="px-1.5 py-0.5 font-bold bg-gold-lt text-ink border border-gold/60 rounded">This facility only</span>
                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() => onChange(behavior.key, masterSetting)}
                        title={`Master: ${describeSetting(masterSetting, isRate, minimumOnly)}`}
                        className="font-bold text-marine hover:text-marine-dk disabled:opacity-50"
                      >
                        Reset
                      </button>
                    </div>
                  ) : (
                    <span className="text-ink-mid">Follows master</span>
                  )}
                  {isOverride && <div className="text-ink-mid mt-0.5">Master: {describeSetting(masterSetting, isRate, minimumOnly)}</div>}
                </div>
              )}

              {overrideCounts && (
                <div className="w-full sm:w-44 text-[11px] text-ink-mid">
                  {overriddenAt === 0 ? 'Every facility follows this' : `${overriddenAt} ${overriddenAt === 1 ? 'facility sets' : 'facilities set'} its own`}
                </div>
              )}
            </li>
          )
        })}
      </ul>

      <div className="flex flex-wrap items-center justify-between gap-3 mt-2 px-1 text-xs">
        <span className="text-ink-mid">
          Weights of the behaviors that are on. <span className="font-bold">100 at</span> and <span className="font-bold">0 at</span> are the levels that earn a value of 100 and 0 when scored.
        </span>
        <span className={`font-bold ${total === FULL_WEIGHT ? 'text-green' : 'text-coral'}`}>
          {total}%{total === FULL_WEIGHT ? '' : ` · ${total < FULL_WEIGHT ? `${Math.round((FULL_WEIGHT - total) * 100) / 100}% short of` : `${Math.round((total - FULL_WEIGHT) * 100) / 100}% over`} 100%`}
        </span>
      </div>
    </div>
  )
}
